// This file is part of Invenio-RDM-Records
// Copyright (C) 2020-2023 CERN.
// Copyright (C) 2020-2022 Northwestern University.
// Copyright (C) 2021 Graz University of Technology.
//
// Invenio-RDM-Records is free software; you can redistribute it and/or modify it
// under the terms of the MIT License; see LICENSE file for more details.

import React, { Component } from "react";
import PropTypes from "prop-types";

import { TextField, SelectField, GroupField } from "react-invenio-forms";
import { i18next } from "@translations/invenio_rdm_records/i18next";
import { emptyLocation } from "./initialValues";
import { Button, Form, Icon } from "semantic-ui-react";
import _isEmpty from "lodash/isEmpty";
import _isEqual from "lodash/isEqual";
import _matches from "lodash/matches";
import _filter from "lodash/filter";
import { LocationArrayField } from "./LocationArrayField";
import { ArrayField } from "../Overrides";

export class LocationsField extends Component {
  getRequiredOption = (currentValue, arrayOfValues) => {
    const { requiredOptions } = this.props;
    for (const requiredOption of requiredOptions) {
      // If more values matched we do take the first value
      const matchingValue = _filter(arrayOfValues, _matches(requiredOption))[0];
      if (_isEqual(matchingValue, currentValue)) {
        return requiredOption;
      }
    }
    return null;
  };

  render() {
    const {
      fieldPath,
      options,
      label,
      labelIcon,
      required,
      requiredOptions,
      showEmptyValue,
    } = this.props;

    const autoBox = true;

    return (
      <ArrayField
        addButtonLabel={i18next.t("Add location")} // TODO: Pass by prop
        defaultNewValue={emptyLocation}
        fieldPath={fieldPath}
        label={label}
        labelIcon={labelIcon}
        required={required}
        requiredOptions={requiredOptions}
        showEmptyValue={showEmptyValue}
        helpText="The locations of the contents of the resource."
      >
        {({ array, arrayHelpers, indexPath, value }) => {
          const fieldPathPrefix = `${fieldPath}.${indexPath}`;
          const requiredOption = this.getRequiredOption(value, array);
          const geometryType = value?.geometry?.type || "";
          return (
            <>
              <GroupField fieldPath={fieldPath} optimized>
                <TextField
                  fieldPath={`${fieldPathPrefix}.place`}
                  label={i18next.t("Place")}
                  width={5}
                />
                <TextField
                  fieldPath={`${fieldPathPrefix}.description`}
                  label={i18next.t("Description")}
                  width={10}
                />
                <Form.Field>
                  <Button
                    aria-label={i18next.t("Remove field")}
                    className="close-btn"
                    icon
                    onClick={() => arrayHelpers.remove(indexPath)}
                    type="button"
                  >
                    <Icon name="close" />
                  </Button>
                </Form.Field>
              </GroupField>
              <GroupField
                fieldPath={fieldPath}
                optimized
                className="flow-wrap invenio-group-field"
              >
                <div className="sixteen wide field">
                  <GroupField fieldPath={fieldPath} optimized>
                    <SelectField
                      fieldPath={`${fieldPathPrefix}.geometry.type`}
                      label={i18next.t("Type")}
                      aria-label={i18next.t("Type")}
                      options={options}
                      width={5}
                      optimized
                    />
                    {geometryType == "Point" && (
                      <>
                        <TextField
                          fieldPath={`${fieldPathPrefix}.geometry.coordinates.0`}
                          label={i18next.t("Longitude")}
                          width={5}
                        />
                        <TextField
                          fieldPath={`${fieldPathPrefix}.geometry.coordinates.1`}
                          label={i18next.t("Latitude")}
                          width={5}
                        />
                      </>
                    )}
                    {geometryType == "Envelope" && (
                      <>
                        <TextField
                          fieldPath={`${fieldPathPrefix}.geometry.coordinates.0.0`}
                          label={i18next.t("NW Longitude")}
                          width={4}
                        />
                        <TextField
                          fieldPath={`${fieldPathPrefix}.geometry.coordinates.0.1`}
                          label={i18next.t("NW Latitude")}
                          width={4}
                        />
                        <TextField
                          fieldPath={`${fieldPathPrefix}.geometry.coordinates.1.0`}
                          label={i18next.t("SE Longitude")}
                          width={4}
                        />
                        <TextField
                          fieldPath={`${fieldPathPrefix}.geometry.coordinates.1.1`}
                          label={i18next.t("SE Latitude")}
                          width={4}
                        />
                      </>
                    )}
                    {geometryType == "MultiPoint" && (
                      <LocationArrayField
                        addButtonLabel={i18next.t("Add Point")} // TODO: Pass by prop
                        defaultNewValue={[]}
                        fieldPath={`${fieldPathPrefix}.geometry.coordinates`}
                        label={i18next.t("Points")}
                        required={required}
                        requiredOptions={requiredOptions}
                        showEmptyValue={showEmptyValue}
                        helpText=""
                        className="eleven wide"
                      >
                        {({ array, arrayHelpers, indexPath, value }) => {
                          const fieldPathPrefix = `${arrayHelpers.name}.${indexPath}`;
                          const requiredOption = this.getRequiredOption(value, array);
                          return (
                            <div className="fields invenio-group-field">
                              <TextField
                                fieldPath={`${fieldPathPrefix}.0`}
                                label={i18next.t("Longitude")}
                                width={7}
                              />
                              <TextField
                                fieldPath={`${fieldPathPrefix}.1`}
                                label={i18next.t("Latitude")}
                                width={7}
                              />
                              <Form.Field className="two wide">
                                <Button
                                  aria-label={i18next.t("Remove point")}
                                  className="close-btn right floated"
                                  icon
                                  onClick={() => arrayHelpers.remove(indexPath)}
                                  type="button"
                                >
                                  <Icon name="close" />
                                </Button>
                              </Form.Field>
                            </div>
                          );
                        }}
                      </LocationArrayField>
                    )}
                    {geometryType == "Polygon" && (
                      <LocationArrayField
                        addButtonLabel={i18next.t("Add polygon/hole")} // TODO: Pass by prop
                        fieldPath={`${fieldPathPrefix}.geometry.coordinates`}
                        label={i18next.t("Polygons")}
                        required={required}
                        requiredOptions={requiredOptions}
                        showEmptyValue={showEmptyValue}
                        defaultNewValue={[[]]}
                        helpText="Polygons, first one is the surrounding polygon, following polygons are holes"
                        className="eleven wide"
                      >
                        {({ array, arrayHelpers, indexPath, value }) => {
                          const fieldPathPrefix = `${arrayHelpers.name}.${indexPath}`;
                          const requiredOption = this.getRequiredOption(value, array);
                          return (
                            <div className="fields invenio-group-field">
                              <LocationArrayField
                                addButtonLabel={i18next.t("Add corner")} // TODO: Pass by prop
                                fieldPath={fieldPathPrefix}
                                label={i18next.t("Corners")}
                                required={required}
                                requiredOptions={requiredOptions}
                                showEmptyValue={showEmptyValue}
                                defaultNewValue={[]}
                                helpText={
                                  indexPath == 0
                                    ? "Corners in anti-clockwise order. First and last point has to be same."
                                    : "Corners in clockwise order. First and last point has to be same."
                                }
                                className="fifteen wide"
                              >
                                {({ array, arrayHelpers, indexPath, value }) => {
                                  const fieldPathPrefix = `${arrayHelpers.name}.${indexPath}`;
                                  const requiredOption = this.getRequiredOption(
                                    value,
                                    array
                                  );
                                  return (
                                    <div className="fields invenio-group-field bg-dark">
                                      <TextField
                                        fieldPath={`${fieldPathPrefix}.0`}
                                        label={i18next.t("Longitude")}
                                        width={7}
                                      />
                                      <TextField
                                        fieldPath={`${fieldPathPrefix}.1`}
                                        label={i18next.t("Latitude")}
                                        width={7}
                                      />
                                      <Form.Field className="two wide">
                                        <Button
                                          aria-label={i18next.t("Remove point")}
                                          className="close-btn right floated"
                                          icon
                                          onClick={() => arrayHelpers.remove(indexPath)}
                                          type="button"
                                        >
                                          <Icon name="close" />
                                        </Button>
                                      </Form.Field>
                                    </div>
                                  );
                                }}
                              </LocationArrayField>
                              <Form.Field className="one wide">
                                <Button
                                  aria-label={i18next.t("Remove point")}
                                  className="close-btn right floated"
                                  icon
                                  onClick={() => arrayHelpers.remove(indexPath)}
                                  type="button"
                                >
                                  <Icon name="close" />
                                </Button>
                              </Form.Field>
                            </div>
                          );
                        }}
                      </LocationArrayField>
                    )}
                  </GroupField>
                </div>
              </GroupField>
            </>
          );
        }}
      </ArrayField>
    );
  }
}

LocationsField.propTypes = {
  fieldPath: PropTypes.string.isRequired,
  label: PropTypes.string,
  required: PropTypes.bool,
  requiredOptions: PropTypes.array,
  showEmptyValue: PropTypes.bool,
  labelIcon: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      value: PropTypes.string,
    })
  ).isRequired,
};

LocationsField.defaultProps = {
  label: i18next.t("Geolocations"),
  required: false,
  requiredOptions: [],
  labelIcon: "compass",
  showEmptyValue: false,
};
